'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery } from 'convex/react';
import { useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { toast } from 'sonner';
import { api } from '@/convex/_generated/api';
import { useProtectedWorkspace } from '@/providers/protected-workspace-boundary';
import { usePersistentVoiceCall } from '@/contexts/persistent-voice-call-context';
import type { PersistentVoiceCall } from '@/lib/persistent-voice-call-state';
import { createYourImaiVoiceLauncher } from '@/lib/your-imai-voice-launcher';
import { isYourImaiArea } from '@/lib/your-imai-routes';
import { getYourImaiCopy } from '@/i18n/your-imai-copy';
import { getYourImaiVoiceCopy } from '@/i18n/your-imai-voice-copy';
import { useYourImaiMoodExpression } from '@/components/your-imai/use-your-imai-persona';
import {
  resolveYourImaiPersona,
  withYourImaiMood,
} from '@/components/your-imai/your-imai-persona';
import { IdleVoiceDock } from './idle-voice-dock';
import { useIdleVoiceAvatarDismissal } from './use-idle-voice-avatar-dismissal';
import { useVoiceCallCreditGate } from '@/components/org-chat/use-voice-call-credit-gate';
import {
  getVoicePreferences,
  setVoicePreferences,
  type VoiceAvatarVisibility,
} from './your-imai-voice-api';

type YourImaiVoiceContextValue = {
  call: PersistentVoiceCall | null;
  isStarting: boolean;
  isCallBlocked: boolean;
  callBlockedMessage: string;
  startCall: () => Promise<void>;
  visibility: VoiceAvatarVisibility;
  setVisibility: (visibility: VoiceAvatarVisibility) => Promise<void>;
  setDockTarget: (target: HTMLElement | null) => void;
};

const YourImaiVoiceContext = createContext<YourImaiVoiceContextValue | null>(
  null
);
type VoiceAdmission = { scope: string | null; orgSlug: string };

/** Workspace launchers share one personal chat and never connect while idle. */
export function YourImaiVoiceProvider({
  orgSlug,
  children,
}: {
  orgSlug: string;
  children: ReactNode;
}) {
  const locale = useLocale();
  const pathname = usePathname();
  const copy = getYourImaiVoiceCopy(locale);
  const workspace = useProtectedWorkspace(orgSlug);
  const creditGate = useVoiceCallCreditGate(workspace ? orgSlug : undefined);
  const org = useQuery(
    api.organizations.queries.getBySlug,
    workspace ? { slug: orgSlug } : 'skip'
  );
  const preferences = useQuery(
    getVoicePreferences,
    workspace ? { orgSlug } : 'skip'
  );
  const imaiState = useQuery(
    api.your_imai.feed.getYourImaiState,
    workspace ? { orgSlug } : 'skip'
  );
  // The same look the sidebar row shows, mood face included.
  const moodExpression = useYourImaiMoodExpression(imaiState?.mood);
  const persona = withYourImaiMood(
    resolveYourImaiPersona(
      imaiState?.persona,
      getYourImaiCopy(locale).navLabel
    ),
    moodExpression
  );
  const ensureChat = useMutation(api.your_imai.chats.ensureYourImaiChat);
  const savePreference = useMutation(setVoicePreferences).withOptimisticUpdate(
    (store, args) => {
      store.setQuery(
        getVoicePreferences,
        { orgSlug: args.orgSlug },
        { visibility: args.visibility }
      );
    }
  );
  const { call, startCall: connect, setDockTarget } = usePersistentVoiceCall();
  const organizationId = org?._id;
  const scope =
    workspace && organizationId
      ? `${workspace.userId}:${organizationId}`
      : null;
  const idleAvatar = useIdleVoiceAvatarDismissal(workspace?.userId ?? null);
  // A new visit has its own fence even when the user returns to the same workspace.
  const admission = useMemo<VoiceAdmission>(
    () => ({ scope, orgSlug }),
    [scope, orgSlug]
  );
  const activeAdmission = useRef<VoiceAdmission | null>(admission);
  const [pendingAdmission, setPendingAdmission] =
    useState<VoiceAdmission | null>(null);
  const isStarting = scope !== null && pendingAdmission === admission;
  const localeRef = useRef(locale);
  useEffect(() => {
    localeRef.current = locale;
  }, [locale]);
  const callRef = useRef(call);
  useEffect(() => {
    callRef.current = call;
  }, [call]);
  useEffect(() => {
    activeAdmission.current = admission;
    return () => {
      if (activeAdmission.current === admission) activeAdmission.current = null;
    };
  }, [admission]);
  const launcherRef = useRef<ReturnType<
    typeof createYourImaiVoiceLauncher
  > | null>(null);
  useEffect(() => {
    const launcher =
      scope && organizationId
        ? createYourImaiVoiceLauncher({
            scope,
            getScope: () =>
              activeAdmission.current === admission ? scope : null,
            hasCall: () => Boolean(callRef.current),
            ensureChat: () => ensureChat({ orgSlug }),
            connect: (chat) => {
              connect(
                {
                  orgThreadId: chat.orgThreadId,
                  organizationId,
                  orgSlug,
                  routeRef: chat.routeId,
                  title: getYourImaiCopy(localeRef.current).navLabel,
                  sourceKind: 'your-imai',
                  positionKey: scope,
                },
                crypto.randomUUID()
              );
            },
          })
        : null;
    launcherRef.current = launcher;
    return () => {
      if (launcherRef.current === launcher) launcherRef.current = null;
    };
  }, [scope, organizationId, ensureChat, orgSlug, connect, admission]);

  const startCall = useCallback(async () => {
    const launcher = launcherRef.current;
    // A low balance still opens the call UI: the call shows the low-credit
    // message and top-up before any mic prompt (#3877).
    if (!launcher || !scope || activeAdmission.current !== admission) return;
    setPendingAdmission(admission);
    try {
      await launcher.start();
    } catch {
      if (activeAdmission.current === admission) toast.error(copy.startFailed);
    } finally {
      setPendingAdmission((pending) =>
        pending === admission ? null : pending
      );
    }
  }, [scope, copy.startFailed, admission]);
  const setVisibility = useCallback(
    async (visibility: VoiceAvatarVisibility) => {
      if (!workspace) return;
      try {
        await savePreference({ orgSlug, visibility });
      } catch {
        toast.error(copy.saveFailed);
      }
    },
    [workspace, savePreference, orgSlug, copy.saveFailed]
  );
  const visibility = preferences?.visibility ?? 'always';
  const isYourImaiPage = isYourImaiArea(pathname, orgSlug);
  const value = useMemo(
    () => ({
      call,
      isStarting,
      isCallBlocked: creditGate.isBlocked,
      callBlockedMessage: creditGate.message,
      startCall,
      visibility,
      setVisibility,
      setDockTarget,
    }),
    [
      call,
      isStarting,
      startCall,
      visibility,
      setVisibility,
      setDockTarget,
      creditGate.isBlocked,
      creditGate.message,
    ]
  );
  return (
    <YourImaiVoiceContext.Provider value={value}>
      {children}
      {scope &&
      preferences &&
      imaiState !== undefined &&
      visibility === 'always' &&
      !idleAvatar.dismissed &&
      !call &&
      !isYourImaiPage ? (
        <IdleVoiceDock
          locale={locale}
          assistantName={persona.name}
          look={persona}
          positionKey={scope}
          isStarting={isStarting}
          isCallBlocked={creditGate.isBlocked}
          callBlockedMessage={creditGate.message}
          onStart={() => {
            void startCall();
          }}
          onDismiss={idleAvatar.dismiss}
        />
      ) : null}
    </YourImaiVoiceContext.Provider>
  );
}

export function useYourImaiVoice() {
  const value = useContext(YourImaiVoiceContext);
  if (!value) throw new Error('Your IMAI voice requires an admitted workspace');
  return value;
}

/** Null outside an admitted workspace, e.g. the shared-chat viewer (#3937). */
export function useYourImaiVoiceOptional() {
  return useContext(YourImaiVoiceContext);
}
